import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "admin" | "partner" | "incorporadora";
}

// Cache em memória entre navegações para evitar "flash" de spinner
// toda vez que o usuário troca de página dentro da área autenticada.
const authCache: Record<string, boolean> = {};

const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const cacheKey = requiredRole || "any";
  const cachedAuthorized = authCache[cacheKey] === true;
  const [isLoading, setIsLoading] = useState(!cachedAuthorized);
  const [isAuthorized, setIsAuthorized] = useState(cachedAuthorized);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate("/login");
          return;
        }

        if (requiredRole) {
          const { data: rolesData, error: rolesError } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", session.user.id);

          // Se a query falhar (ex.: erro de rede transitório), não deslogar.
          // Manter sessão autorizada e deixar a página tentar novamente.
          if (rolesError) {
            console.warn("Falha ao buscar roles (mantendo sessão):", rolesError);
            setIsAuthorized(true);
            return;
          }

          const userRoles = rolesData?.map(r => r.role) || [];
          
          if (!userRoles.includes(requiredRole)) {
            // Redirect based on what roles they have
            if (userRoles.includes("admin")) {
              navigate("/admin");
            } else if (userRoles.includes("incorporadora")) {
              navigate("/incorporadora");
            } else if (userRoles.includes("partner")) {
              navigate("/dashboard");
            } else {
              navigate("/login");
            }
            return;
          }


          // For partners, check if they have an active/authorized subscription
          // Skip this check if they're already on the payment page
          if (requiredRole === "partner" && location.pathname !== "/pagamento-pendente") {
            // First check if partner is on a free plan
            const { data: partner } = await supabase
              .from("partners")
              .select("plano, parent_partner_id")
              .eq("id", session.user.id)
              .maybeSingle();

            // Corretores vinculados a uma imobiliária NÃO precisam de assinatura própria
            const isLinkedBroker = !!partner?.parent_partner_id;

            // Check if they have a paid plan that requires subscription
            const { data: plans } = await supabase
              .from("partner_plans")
              .select("id, price, plan")
              .eq("active", true);

            const partnerPlan = plans?.find(p => p.plan === partner?.plano);
            const needsPayment = partnerPlan && partnerPlan.price && partnerPlan.price > 0;

            if (!isLinkedBroker && (needsPayment || !partner?.plano)) {
              // Check if they have an active subscription
              const { data: subscription } = await supabase
                .from("subscriptions")
                .select("status, end_date")
                .eq("partner_id", session.user.id)
                .in("status", ["authorized", "trial"])
                .limit(1)
                .maybeSingle();

              if (!subscription) {
                navigate("/pagamento-pendente");
                return;
              }

              // Check if trial has expired
              if (subscription.status === "trial" && subscription.end_date) {
                const endDate = new Date(subscription.end_date);
                if (endDate < new Date()) {
                  // Update expired trial status
                  await supabase
                    .from("subscriptions")
                    .update({ status: "expired" })
                    .eq("partner_id", session.user.id)
                    .eq("status", "trial");
                  navigate("/pagamento-pendente");
                  return;
                }
              }
            }
          }
        }

        authCache[cacheKey] = true;
        setIsAuthorized(true);
      } catch (error: any) {
        // Não deslogar por erros transitórios (AbortError ao trocar de rota,
        // falha de rede momentânea, etc). O onAuthStateChange já cuida de
        // SIGNED_OUT real.
        const isAbort =
          error?.name === "AbortError" ||
          /aborted|abort/i.test(error?.message || "");
        if (isAbort) {
          setIsAuthorized(true);
        } else {
          console.error("Auth check error (mantendo sessão):", error);
          setIsAuthorized(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        // Invalida cache para qualquer role no logout
        Object.keys(authCache).forEach((k) => delete authCache[k]);
        navigate("/login");
      }
    });

    checkAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, requiredRole, location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return isAuthorized ? <>{children}</> : null;
};

export default ProtectedRoute;
